"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

interface VisibilityContextType {
  isFooterVisible: boolean;
  isOmniboxVisible: boolean;
}

const VisibilityContext = createContext<VisibilityContextType | null>(null);

export function useAgentVisibility() {
  const context = useContext(VisibilityContext);
  if (!context) {
    throw new Error("useAgentVisibility must be used within AgentVisibilityProvider");
  }
  return context;
}

export function AgentVisibilityProvider({ children }: { children: ReactNode }) {
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [isOmniboxVisible, setIsOmniboxVisible] = useState(false);
  const footerObserverRef = useRef<IntersectionObserver | null>(null);
  const omniboxObserverRef = useRef<IntersectionObserver | null>(null);

  const handleFooterIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    // If any footer is intersecting, hide the agent
    const anyFooterVisible = entries.some(entry => entry.isIntersecting);
    setIsFooterVisible(anyFooterVisible);
  }, []);

  const handleOmniboxIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    // If any omnibox is intersecting with the bottom-right area (where agent sits), hide the agent
    const anyIntersecting = entries.some(entry => entry.isIntersecting);
    setIsOmniboxVisible(anyIntersecting);
  }, []);

  useEffect(() => {
    // Create footer intersection observer
    footerObserverRef.current = new IntersectionObserver(handleFooterIntersection, {
      threshold: 0,
      rootMargin: "0px 0px -100px 0px", // Start hiding when footer is 100px from viewport bottom
    });

    // Create omnibox intersection observer - hide agent when omnibox is in bottom-right corner
    omniboxObserverRef.current = new IntersectionObserver(handleOmniboxIntersection, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      // Shrink viewport to approximate agent's position (bottom-right corner)
      // Format: "top right bottom left"
      rootMargin: "-70% -70% 0px 0px", // Only check bottom-right ~30% area
    });

    const observeElements = () => {
      // Observe footer
      const footer = document.querySelector("footer");
      if (footer && footerObserverRef.current) {
        footerObserverRef.current.observe(footer);
      }

      // Observe omnibox
      const omnibox = document.querySelector(".omnibox-wrapper");
      if (omnibox && omniboxObserverRef.current) {
        omniboxObserverRef.current.observe(omnibox);
      }
    };

    // Try immediately
    observeElements();

    // Retry after a short delay in case elements aren't in DOM yet
    const timeoutId = setTimeout(observeElements, 500);

    return () => {
      clearTimeout(timeoutId);
      if (footerObserverRef.current) {
        footerObserverRef.current.disconnect();
      }
      if (omniboxObserverRef.current) {
        omniboxObserverRef.current.disconnect();
      }
    };
  }, [handleFooterIntersection, handleOmniboxIntersection]);

  return (
    <VisibilityContext.Provider value={{ isFooterVisible, isOmniboxVisible }}>{children}</VisibilityContext.Provider>
  );
}
